// Dashboard del Portero — herramienta de trabajo real, no solo stats.
// Incluye: link + QR descargable + WhatsApp share + listado vecinos + historial comisiones + recursos + IA coach.

import { useEffect, useRef, useState } from 'react'
import Layout from '../../components/Layout'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'

// QRCode lib via CDN (igual que admin/Etiquetas.jsx)
function loadQRCodeLib() {
  return new Promise((resolve, reject) => {
    if (window.QRCode) return resolve(window.QRCode)
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'
    s.onload = () => resolve(window.QRCode)
    s.onerror = reject
    document.body.appendChild(s)
  })
}

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

const ESTADO_PILL = {
  activa:    { bg: '#d1fae5', color: '#065f46', label: '✓ Activo' },
  pausada:   { bg: '#fef3c7', color: '#92400e', label: '⏸ Pausada' },
  cancelada: { bg: '#fee2e2', color: '#991b1b', label: '✗ Cancelada' },
}

const ESTADO_COMISION = {
  pendiente:         { bg: '#fef3c7', color: '#92400e', label: '⏳ Pendiente' },
  hold_verificacion: { bg: '#e0e7ff', color: '#3730a3', label: '🔒 En verificación' },
  pagada:            { bg: '#d1fae5', color: '#065f46', label: '✓ Pagada' },
  cancelada:         { bg: '#fee2e2', color: '#991b1b', label: '✗ Cancelada' },
}

const MONTO_MIN_LIQUIDACION = 1000

export default function PorteroDashboard() {
  const { user, profile } = useAuth()
  const [stats, setStats] = useState(null)
  const [portero, setPortero] = useState(null)
  const [vecinos, setVecinos] = useState([])
  const [comisiones, setComisiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  // Saludo personalizado del portero (editable + guardado en localStorage)
  const [saludo, setSaludo] = useState(() => {
    try { return localStorage.getItem('portero_saludo') || '' } catch { return '' }
  })
  const qrContainerRef = useRef(null)
  const qrInstanceRef = useRef(null)

  useEffect(() => {
    if (!user?.id) return
    cargar()
  }, [user?.id])

  async function cargar() {
    setLoading(true)
    try {
      const [
        { data: dash },
        { data: p },
        { data: vec },
        { data: com },
      ] = await Promise.all([
        supabase.rpc('dashboard_portero'),
        supabase.from('porteros').select('slug, lugar, banco_cuenta, activo').eq('id', user.id).maybeSingle(),
        // ⚠️ PRIVACIDAD: usamos RPC SECURITY DEFINER que SOLO devuelve (id, estado, fecha_inicio).
        // Las RLS bloquean query directa del portero a la tabla clientes. La RPC es el único camino.
        supabase.rpc('portero_get_mis_vecinos'),
        supabase
          .from('comisiones')
          .select('id, mes, monto_total, cantidad_clientes, estado, fecha_pago, referencia_pago, hold_hasta, hold_motivo')
          .eq('portero_id', user.id)
          .order('mes', { ascending: false }),
      ])
      setStats(dash?.[0] ?? null)
      setPortero(p ?? null)
      setVecinos(vec || [])
      setComisiones(com || [])
    } catch (e) {
      console.error('cargar portero:', e)
    } finally {
      setLoading(false)
    }
  }

  const refUrl = portero?.slug ? `https://limpack.uy/?ref=${portero.slug}` : null
  const nombrePortero = profile?.nombre || 'vecino'

  // Render QR cuando hay link
  useEffect(() => {
    if (!refUrl || !qrContainerRef.current) return
    let cancel = false
    ;(async () => {
      try {
        await loadQRCodeLib()
        if (cancel || !qrContainerRef.current) return
        // limpiar QR previo
        qrContainerRef.current.innerHTML = ''
        qrInstanceRef.current = new window.QRCode(qrContainerRef.current, {
          text: refUrl,
          width: 240,
          height: 240,
          colorDark: '#0f1f3d',
          colorLight: '#ffffff',
          correctLevel: window.QRCode.CorrectLevel.M,
        })
      } catch (e) {
        console.error('QR error:', e)
      }
    })()
    return () => { cancel = true }
  }, [refUrl])

  function copiarLink() {
    if (!refUrl) return
    navigator.clipboard.writeText(refUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Saludo por defecto si el portero no editó nada
  const saludoDefault = saludo.trim()
    || `¡Hola! Soy ${nombrePortero}, portero del edificio. Les paso algo que les puede interesar 👇`

  function buildWhatsAppMsg() {
    return [
      saludoDefault,
      '',
      '*Limpack* — productos de limpieza por suscripción mensual.',
      'Te arman un kit a tu medida y te lo dejan en la portería.',
      '',
      '✅ Sin contratos, lo cancelás cuando quieras',
      '✅ Pagás con tarjeta o transferencia',
      '✅ Te ahorrás ir al super y cargar bolsas',
      '✅ Productos de calidad, marcas conocidas',
      '',
      'Si te sumás con mi link queda registrado que sos del edificio:',
      refUrl,
      '',
      'Cualquier cosa me preguntan 🙌',
    ].join('\n')
  }

  function guardarSaludo(nuevo) {
    setSaludo(nuevo)
    try { localStorage.setItem('portero_saludo', nuevo) } catch {}
  }

  function compartirWhatsApp() {
    window.open(`https://wa.me/?text=${encodeURIComponent(buildWhatsAppMsg())}`, '_blank')
  }

  function descargarQR() {
    const canvas = qrContainerRef.current?.querySelector('canvas')
    if (!canvas) return
    const link = document.createElement('a')
    link.download = `limpack-qr-${portero?.slug || 'mi-edificio'}.png`
    link.href = canvas.toDataURL('image/png')
    link.click()
  }

  function imprimirQR() {
    const canvas = qrContainerRef.current?.querySelector('canvas')
    if (!canvas) return
    const dataUrl = canvas.toDataURL('image/png')
    const w = window.open('', '_blank')
    w.document.write(`
      <html>
        <head><title>QR Limpack — ${portero?.slug}</title></head>
        <body style="margin:0;padding:40px;text-align:center;font-family:system-ui,sans-serif;">
          <h1 style="color:#0f1f3d;margin:0 0 8px;">🧼 Limpack</h1>
          <p style="color:#475569;margin:0 0 24px;">Productos de limpieza · Suscripción mensual</p>
          <img src="${dataUrl}" style="max-width:400px;width:80%;border:8px solid #0f1f3d;border-radius:16px;" />
          <p style="margin:24px 0 0;font-size:18px;color:#0f1f3d;font-weight:700;">Escaneá y suscribite</p>
          <p style="margin:8px 0 0;font-size:14px;color:#64748b;">Te lo dejo en la portería cada mes</p>
          <p style="margin:32px 0 0;font-size:11px;color:#94a3b8;">${refUrl}</p>
          <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); }<\/script>
        </body>
      </html>
    `)
  }

  // Stats derivadas
  const vecinosActivos = vecinos.filter(v => v.estado === 'activa').length
  const vecinosTotal = vecinos.length
  const comisionPendiente = comisiones.filter(c => c.estado === 'pendiente').reduce((s, c) => s + Number(c.monto_total), 0)

  return (
    <Layout>
      <div className="po-app">
        {/* Header */}
        <div className="po-header">
          <div>
            <h1 style={{ margin: 0 }}>🛎️ ¡Hola, {nombrePortero}!</h1>
            <p className="lead" style={{ margin: '4px 0 0' }}>
              {portero?.lugar
                ? <>Tu edificio: <strong>{portero.lugar}</strong></>
                : 'Tu panel de Vecino Limpack'}
            </p>
          </div>
        </div>

        {!loading && !portero && (
          <div className="card" style={{ background: '#FEF3C7', borderColor: '#F59E0B' }}>
            <h3 style={{ color: '#92400E', margin: '0 0 6px' }}>⚠️ Sin slug asignado</h3>
            <p style={{ color: '#78350F', fontSize: 13, margin: 0 }}>
              Pedile al admin de Limpack que active tu cuenta de portero (asignar slug).
              Sin slug no podés tener tu link único.
            </p>
          </div>
        )}

        {/* Bloque Link + QR — mobile-first: WA primero, QR después */}
        {portero && refUrl && (
          <div className="po-link-card">
            <div className="po-link-info">
              <div className="po-tag">🔗 Tu herramienta principal</div>
              <h2 className="po-link-titulo">Tu link único</h2>
              <div className="po-link-url" onClick={copiarLink} title="Click para copiar">
                <code>{refUrl}</code>
                <span className="po-copy-badge">{copied ? '✓ Copiado' : '📋 Copiar'}</span>
              </div>
              {/* WhatsApp es el botón principal — más grande y solo en mobile */}
              <button className="po-btn po-btn-wa po-btn-hero" onClick={compartirWhatsApp}>
                💬 Compartir por WhatsApp
              </button>
              <div className="po-link-acciones-secundarias">
                <button className="po-btn po-btn-ghost" onClick={descargarQR}>
                  ⬇️ Descargar QR
                </button>
                <button className="po-btn po-btn-ghost" onClick={imprimirQR}>
                  🖨️ Cartel A4
                </button>
              </div>
              <div className="po-link-tip">
                💡 Cuando un vecino escanee tu QR o entre por este link, queda atribuido a vos automáticamente.
              </div>
            </div>
            <div className="po-qr-wrap">
              <div ref={qrContainerRef} className="po-qr"></div>
              <div className="po-qr-label">Tu QR · {portero.slug}</div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="po-stats">
          <div className="po-stat">
            <div className="po-stat-label">Vecinos activos</div>
            <div className="po-stat-value" style={{ color: '#10B981' }}>{vecinosActivos}</div>
            <div className="po-stat-sub">{vecinosTotal} en total</div>
          </div>
          <div className="po-stat">
            <div className="po-stat-label">Comisión mes en curso</div>
            <div className="po-stat-value">{fmtUYU(stats?.comision_mes_actual)}</div>
            <div className="po-stat-sub">se liquida a fin de mes</div>
          </div>
          <div className="po-stat">
            <div className="po-stat-label">Mes anterior</div>
            <div className="po-stat-value" style={{ color: '#3b82f6' }}>{fmtUYU(stats?.comision_mes_anterior)}</div>
            <div className="po-stat-sub">próxima liquidación</div>
          </div>
          <div className="po-stat">
            <div className="po-stat-label">Total cobrado histórico</div>
            <div className="po-stat-value" style={{ color: '#1a3461' }}>{fmtUYU(stats?.total_pagado_historico)}</div>
            <div className="po-stat-sub">desde que sumaste</div>
          </div>
        </div>

        {comisionPendiente >= MONTO_MIN_LIQUIDACION && (
          <div className="po-alerta">
            💰 Tenés <strong>{fmtUYU(comisionPendiente)}</strong> pendientes de transferencia (superan el mínimo de {fmtUYU(MONTO_MIN_LIQUIDACION)}).
            Si pasaron más de 5 días hábiles, escribile al admin para coordinar el pago.
          </div>
        )}

        {/* Tarifa de comisión por tipo de kit (educativo) */}
        <div className="po-tarifas">
          <div className="po-tarifas-titulo">💵 Cuánto ganás por mes y por vecino</div>
          <div className="po-tarifas-grid">
            <div className="po-tarifa">
              <div className="po-tarifa-monto">$50</div>
              <div className="po-tarifa-label">Kit Essential</div>
              <div className="po-tarifa-detalle">Solo · Familiar · Hogar+</div>
            </div>
            <div className="po-tarifa">
              <div className="po-tarifa-monto" style={{ color: '#0891b2' }}>$100</div>
              <div className="po-tarifa-label">Kit Premium</div>
              <div className="po-tarifa-detalle">Dúo · Confort · Exclusive</div>
            </div>
          </div>
          <div className="po-tarifas-nota">
            Cobrás <strong>todos los meses</strong> mientras el vecino siga suscripto y al día.
            Compras puntuales o productos sueltos NO generan comisión — solo las suscripciones a kits.
          </div>
        </div>

        {/* Mis Vecinos */}
        <div className="po-seccion">
          <h2>👥 Mis vecinos referidos ({vecinos.length})</h2>
          {loading && <div className="po-loading">Cargando…</div>}
          {!loading && vecinos.length === 0 && (
            <div className="po-empty">
              <div style={{ fontSize: 48 }}>🌱</div>
              <h3>Todavía no tenés vecinos suscritos</h3>
              <p>Compartí tu link y QR en el grupo del edificio. Mirá la sección "💡 Cómo sumar vecinos" abajo.</p>
            </div>
          )}
          {!loading && vecinos.length > 0 && (
            <>
              <div className="po-privacy-note">
                🔒 <strong>Privacidad:</strong> no podés ver nombres, productos ni kits.
                Solo cantidad, estado y mes — que es la base de tu comisión.
              </div>

              {/* Tabla en desktop, cards en mobile */}
              <div className="po-vecinos-list">
                {vecinos.map((v, i) => {
                  const pill = ESTADO_PILL[v.estado] || ESTADO_PILL.activa
                  const altaMes = v.fecha_inicio
                    ? new Date(v.fecha_inicio + 'T12:00:00').toLocaleDateString('es-UY', { month: 'short', year: '2-digit' })
                    : '—'
                  const meses = v.fecha_inicio
                    ? Math.max(0, Math.round((new Date() - new Date(v.fecha_inicio)) / (1000 * 60 * 60 * 24 * 30)))
                    : 0
                  const antig = meses === 0 ? 'Este mes' : meses === 1 ? '1 mes' : `${meses} meses`
                  return (
                    <div key={v.id} className="po-vecino-card">
                      <div className="po-vecino-num">#{vecinos.length - i}</div>
                      <div className="po-vecino-info">
                        <div className="po-vecino-mes">{altaMes}</div>
                        <div className="po-vecino-antig">{antig}</div>
                      </div>
                      <span className="po-pill" style={{ background: pill.bg, color: pill.color }}>
                        {pill.label}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Historial Comisiones */}
        <div className="po-seccion">
          <h2>💰 Mis comisiones</h2>
          {comisiones.length === 0 && (
            <div className="po-empty" style={{ padding: 24 }}>
              <p>Cuando tengas vecinos pagando, acá aparece tu historial mensual.</p>
            </div>
          )}
          {comisiones.length > 0 && (
            <div className="po-comis-list">
              {comisiones.map(c => {
                const pill = ESTADO_COMISION[c.estado] || ESTADO_COMISION.pendiente
                const mesLabel = c.mes
                  ? new Date(c.mes + 'T12:00:00').toLocaleDateString('es-UY', { month: 'long', year: 'numeric' })
                  : c.mes
                const enHold = c.estado === 'hold_verificacion'
                return (
                  <div key={c.id} className="po-comis-card">
                    <div className="po-comis-head">
                      <div>
                        <div className="po-comis-mes">{mesLabel}</div>
                        <div className="po-comis-vec">{c.cantidad_clientes} vecino{c.cantidad_clientes !== 1 ? 's' : ''}</div>
                      </div>
                      <div className="po-comis-monto">{fmtUYU(c.monto_total)}</div>
                    </div>
                    <div className="po-comis-foot">
                      <span className="po-pill" style={{ background: pill.bg, color: pill.color }}>
                        {pill.label}
                      </span>
                      {c.fecha_pago && (
                        <span className="po-comis-fecha">Pagada el {fmtFecha(c.fecha_pago)}</span>
                      )}
                      {enHold && c.hold_hasta && (
                        <span className="po-comis-fecha">Liberación: {fmtFecha(c.hold_hasta)}</span>
                      )}
                    </div>
                    {enHold && c.hold_motivo && (
                      <div className="po-comis-hold-info">
                        ℹ️ {c.hold_motivo}
                      </div>
                    )}
                    {c.referencia_pago && (
                      <div className="po-comis-ref">Ref: {c.referencia_pago}</div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Recursos para vender */}
        {portero && refUrl && (
          <div className="po-seccion">
            <h2>💡 Cómo sumar más vecinos</h2>
            <div className="po-tips-grid">
              <div className="po-tip">
                <div className="po-tip-icon">📱</div>
                <h3>Mensaje para grupo de WhatsApp</h3>

                <div style={{ marginTop: 10, marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    Cómo te presentás (editable):
                  </label>
                  <input
                    type="text"
                    value={saludo}
                    onChange={e => guardarSaludo(e.target.value)}
                    placeholder={`¡Hola! Soy ${nombrePortero}, portero del edificio. Les paso algo que les puede interesar 👇`}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      marginTop: 4, padding: '8px 10px',
                      border: '1px solid #d1d5db', borderRadius: 8,
                      fontSize: 12, fontFamily: 'inherit',
                    }}
                  />
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                    💡 Probá con tu rol real: "soy el portero", "encargado de seguridad", "el de la portería de la mañana"…
                  </div>
                </div>

                <p style={{ fontSize: 12, color: '#475569', marginTop: 10 }}>
                  Vista previa del mensaje:
                </p>
                <div className="po-modelo-msg">{buildWhatsAppMsg()}</div>
                <button className="po-btn po-btn-wa" onClick={compartirWhatsApp} style={{ marginTop: 12 }}>
                  💬 Abrir WhatsApp con este mensaje
                </button>
              </div>

              <div className="po-tip">
                <div className="po-tip-icon">🚪</div>
                <h3>Cartel para portería</h3>
                <p style={{ fontSize: 13, color: '#475569', marginTop: 8 }}>
                  Imprimí tu QR en hoja A4 y dejalo en un lugar visible (corcho, puerta del depósito, palier).
                </p>
                <button className="po-btn po-btn-ghost" onClick={imprimirQR} style={{ marginTop: 12 }}>
                  🖨️ Imprimir cartel ahora
                </button>
              </div>

              <div className="po-tip">
                <div className="po-tip-icon">🤝</div>
                <h3>Hablá cara a cara</h3>
                <p style={{ fontSize: 13, color: '#475569', marginTop: 8 }}>
                  Cuando subas paquetes con un vecino, contale en 30 segundos:
                </p>
                <ul style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.7 }}>
                  <li>"¿Conocés Limpack? Productos de limpieza por suscripción"</li>
                  <li>"Te arman un kit con lo que necesitás y te lo dejo en la portería"</li>
                  <li>"Sin contratos, lo cancelás cuando quieras"</li>
                  <li>"Si te interesa, escaneá mi QR — soy el referido del edificio"</li>
                </ul>
              </div>

              <div className="po-tip">
                <div className="po-tip-icon">🎁</div>
                <h3>Aprovechá los buenos momentos</h3>
                <p style={{ fontSize: 13, color: '#475569', marginTop: 8 }}>
                  Mudanzas, vueltas de vacaciones, fines de mes — son los mejores momentos para mencionarles Limpack.
                  Si los ves cargando bolsas del super, contales: <em>"esto te lo dejan en la portería todos los meses, fijate"</em>.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>

      <style>{`
        /* ════ APP MOBILE ════ Wrapper tipo "app de celular" — mismo formato que Mi día del repartidor */
        .po-app {
          max-width: 480px;
          margin: 0 auto;
          padding: 16px;
          padding-bottom: 80px;
          color: #111;
        }
        .po-header { margin-bottom: 14px; }

        /* Card link+QR — en mobile: orden vertical, QR DEBAJO del link */
        .po-link-card {
          display: flex; flex-direction: column; gap: 16px;
          background: linear-gradient(135deg, #0f1f3d 0%, #1a3461 100%);
          color: #fff;
          padding: 18px; border-radius: 16px;
          margin-bottom: 20px;
          box-shadow: 0 12px 32px rgba(15,31,61,.18);
        }
        .po-link-info { order: 1; }
        .po-qr-wrap { order: 2; align-self: center; }

        .po-tag { font-size: 10px; opacity: .7; text-transform: uppercase; letter-spacing: .08em; font-weight: 700; }
        .po-link-titulo { font-size: 20px; font-weight: 800; margin: 4px 0 14px; }
        .po-link-url {
          background: rgba(255,255,255,.1);
          border: 1px solid rgba(255,255,255,.2);
          border-radius: 10px;
          padding: 12px 14px;
          display: flex; justify-content: space-between; align-items: center;
          gap: 10px; cursor: pointer;
          margin-bottom: 12px;
        }
        .po-link-url:active { background: rgba(255,255,255,.2); }
        .po-link-url code { font-size: 13px; word-break: break-all; color: #fff; }
        .po-copy-badge {
          background: #00c4d4; color: #0f1f3d;
          padding: 5px 11px; border-radius: 999px;
          font-size: 11px; font-weight: 800; white-space: nowrap;
          flex-shrink: 0;
        }

        .po-btn {
          min-height: 44px; padding: 11px 18px;
          border: 0; border-radius: 999px;
          font-weight: 700; font-size: 14px; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center; gap: 6px;
          font-family: inherit;
        }
        .po-btn-wa { background: #25d366; color: #fff; }
        .po-btn-wa:active { background: #1ebe5a; }
        .po-btn-hero {
          width: 100%; min-height: 52px; font-size: 16px; margin-bottom: 10px;
          box-shadow: 0 4px 14px rgba(37, 211, 102, .35);
        }
        .po-btn-ghost {
          background: rgba(255,255,255,.1); color: #fff;
          border: 1px solid rgba(255,255,255,.25);
          flex: 1; min-width: 0;
        }
        .po-btn-ghost:active { background: rgba(255,255,255,.2); }
        .po-link-acciones-secundarias { display: flex; gap: 8px; }
        .po-link-tip { font-size: 12px; opacity: .8; margin-top: 12px; line-height: 1.5; }

        .po-qr-wrap { background: #fff; padding: 12px; border-radius: 12px; display: flex; flex-direction: column; align-items: center; }
        .po-qr { display: block; }
        .po-qr-label { font-size: 11px; color: #475569; margin-top: 6px; font-weight: 700; }

        /* Stats — 2 columnas en mobile */
        .po-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 14px; }
        .po-stat { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; }
        .po-stat-label { font-size: 10px; color: #6b7280; text-transform: uppercase; font-weight: 700; letter-spacing: .04em; }
        .po-stat-value { font-size: 22px; font-weight: 900; color: #1a3461; margin: 4px 0 2px; line-height: 1.1; }
        .po-stat-sub { font-size: 10px; color: #94a3b8; }

        .po-alerta {
          background: #fef3c7; border: 1px solid #fcd34d;
          color: #78350f; padding: 12px 14px; border-radius: 10px;
          font-size: 13px; margin-bottom: 14px; line-height: 1.5;
        }
        .po-tarifas {
          background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 12px;
          padding: 14px; margin-bottom: 16px;
        }
        .po-tarifas-titulo { font-weight: 800; color: #065f46; font-size: 14px; margin-bottom: 10px; }
        .po-tarifas-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .po-tarifa { background: #fff; border: 1px solid #d1fae5; border-radius: 10px; padding: 10px 12px; text-align: center; }
        .po-tarifa-monto { font-size: 24px; font-weight: 900; color: #10B981; line-height: 1; }
        .po-tarifa-label { font-size: 12px; font-weight: 700; color: #065f46; margin-top: 4px; }
        .po-tarifa-detalle { font-size: 10px; color: #047857; margin-top: 2px; }
        .po-tarifas-nota { font-size: 11px; color: #047857; margin-top: 10px; line-height: 1.5; }

        .po-seccion { margin-top: 28px; }
        .po-seccion h2 { color: #1a3461; font-size: 17px; margin-bottom: 10px; }

        .po-loading { padding: 30px; text-align: center; color: #6b7280; }
        .po-empty { padding: 32px 16px; text-align: center; background: #fff; border: 1px dashed #cbd5e1; border-radius: 12px; color: #6b7280; }
        .po-empty h3 { color: #1a3461; margin: 12px 0 6px; }

        .po-privacy-note {
          background: #f0f9ff; border: 1px solid #bae6fd;
          color: #075985; padding: 10px 12px; border-radius: 8px;
          font-size: 12px; line-height: 1.5; margin-bottom: 10px;
        }
        .po-pill { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; white-space: nowrap; }

        /* Vecinos — cards apiladas, no tabla */
        .po-vecinos-list { display: flex; flex-direction: column; gap: 8px; }
        .po-vecino-card {
          background: #fff; border: 1px solid #e5e7eb; border-radius: 10px;
          padding: 12px 14px;
          display: flex; align-items: center; gap: 12px;
        }
        .po-vecino-num {
          background: #eff6ff; color: #1e40af;
          width: 40px; height: 40px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; font-size: 13px;
          flex-shrink: 0;
        }
        .po-vecino-info { flex: 1; min-width: 0; }
        .po-vecino-mes { font-weight: 700; color: #1a3461; font-size: 14px; text-transform: capitalize; }
        .po-vecino-antig { font-size: 11px; color: #94a3b8; margin-top: 2px; }

        /* Comisiones — cards apiladas */
        .po-comis-list { display: flex; flex-direction: column; gap: 10px; }
        .po-comis-card {
          background: #fff; border: 1px solid #e5e7eb; border-radius: 10px;
          padding: 14px;
        }
        .po-comis-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
        .po-comis-mes { font-weight: 800; color: #1a3461; font-size: 15px; text-transform: capitalize; }
        .po-comis-vec { font-size: 12px; color: #6b7280; margin-top: 2px; }
        .po-comis-monto { font-weight: 900; color: #10B981; font-size: 22px; font-variant-numeric: tabular-nums; }
        .po-comis-foot { display: flex; align-items: center; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
        .po-comis-fecha { font-size: 11px; color: #64748b; }
        .po-comis-ref { margin-top: 8px; font-size: 11px; font-family: monospace; color: #94a3b8; word-break: break-all; }
        .po-comis-hold-info {
          margin-top: 10px; padding: 10px 12px;
          background: #eef2ff; border-left: 3px solid #6366f1;
          border-radius: 0 8px 8px 0;
          font-size: 12px; color: #3730a3; line-height: 1.5;
        }

        /* Tips para vender — 1 columna en mobile */
        .po-tips-grid { display: flex; flex-direction: column; gap: 10px; }
        .po-tip { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px; }
        .po-tip-icon { font-size: 24px; margin-bottom: 4px; }
        .po-tip h3 { color: #1a3461; font-size: 14px; margin: 4px 0 0; }
        .po-modelo-msg {
          margin-top: 8px; padding: 12px 14px;
          background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px;
          font-size: 12px; color: #14532d; line-height: 1.6;
          white-space: pre-line;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          word-break: break-word;
        }

      `}</style>
    </Layout>
  )
}
